/**
 * «Информация о проекте» из меню «⋯» столбца на доске с проектом
 * для каждого столбца.
 *
 * Два поля: «Название» проекта — только для чтения у всех, его правят
 * в справочнике проектов; «Информация» — свой текст у каждой доски,
 * хранится у столбца. Читают её все участники доски, правит только
 * руководитель (право board.columns — то же, что у остальных правок
 * столбца, поэтому сервер проверяет его сам, а интерфейс лишь не
 * предлагает лишнего).
 */

import { useState } from "react";

import { errorMessage } from "@taskflow/client-core";
import { Button, FormField, Input, Modal, Textarea, toast } from "@/shared/ui";

import { type BoardColumn, useUpdateColumnMutation } from "@taskflow/client-core/boards";

export function ProjectInfoDialog({
  boardId,
  column,
  canEdit,
  onClose,
}: {
  boardId: string;
  column: BoardColumn;
  canEdit: boolean;
  onClose: () => void;
}) {
  const [update, { isLoading: busy }] = useUpdateColumnMutation();
  const [info, setInfo] = useState(column.projectInfo ?? "");
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    try {
      await update({
        boardId,
        columnId: column.id,
        version: column.version,
        projectInfo: info,
      }).unwrap();
      toast.success("Изменения сохранены", column.projectName ?? undefined);
      onClose();
    } catch (raw) {
      // Параллельная правка столбца — отказ сервера по существу.
      setError(errorMessage(raw));
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Информация о проекте"
      width="sm"
      dismissable={!busy}
      footer={
        canEdit ? (
          <>
            <Button variant="secondary" onClick={onClose} disabled={busy}>
              Отмена
            </Button>
            <Button loading={busy} onClick={() => void submit()}>
              Сохранить
            </Button>
          </>
        ) : (
          <Button variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
        )
      }
    >
      <div className="flex flex-col gap-3.5">
        <FormField label="Название">
          {(props) => (
            <Input {...props} value={column.projectName ?? ""} readOnly className="bg-sunken" />
          )}
        </FormField>
        <FormField label="Информация">
          {(props) =>
            canEdit ? (
              <Textarea
                {...props}
                value={info}
                rows={6}
                autoFocus
                onChange={(event) => setInfo(event.target.value)}
              />
            ) : (
              <Textarea
                {...props}
                value={info}
                rows={6}
                readOnly
                placeholder="Не заполнена"
                className="resize-none bg-sunken"
              />
            )
          }
        </FormField>
        {error !== null && (
          <p role="alert" className="text-[13px] font-semibold text-danger">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
